import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { DateTime } from 'luxon';
import { CompanyUserTodo } from '../../interfaces/company-user-todo';
import * as TodoActions from './todos.actions';

export const todosFeatureKey = 'todos';

export interface State extends EntityState<CompanyUserTodo> {
  loading: boolean;
  lastPoll: number;
  count: number;
  open: number;
  error: unknown;
}

export const adapter: EntityAdapter<CompanyUserTodo> = createEntityAdapter<CompanyUserTodo>();

export const initialState: State = adapter.getInitialState({
  loading: false,
  lastPoll: 0,
  count: 0,
  open: 0,
  error: null,
});

export const todosReducer = createReducer(
  initialState,
  on(TodoActions.loadTodosLazy, (state) => ({ ...state, loading: true })),
  on(TodoActions.loadTodosPolling, (state) => ({ ...state, loading: true })),
  on(TodoActions.loadTodosLazySuccess, (state, action) =>
    adapter.upsertMany(action.todos, {
      ...state,
      loading: false,
      open: action.undone,
      count: action.overall,
    })
  ),
  on(TodoActions.loadTodosPollingSuccess, (state, action) =>
    adapter.upsertMany(action.todos, {
      ...state,
      loading: false,
      open: state.open + action.undone,
      count: state.count + action.overall,
      lastPoll: Math.round(DateTime.now().toSeconds()) + 1,
    })
  ),
  on(TodoActions.loadTodosFailure, (state, action) => ({ ...state, error: action.error, loading: false })),
  on(TodoActions.deleteTodoSuccess, (state, action) =>
    adapter.removeOne(action.todo.id, { ...state, open: Math.max(0, state.open - 1) })
  ),
  on(TodoActions.deleteTodoFailure, (state, action) => ({ ...state, error: action.error })),
  on(TodoActions.deleteAllTodosSuccess, (state, action) => initialState),
  on(TodoActions.deleteAllTodosFailure, (state, action) => ({ ...state, error: action.error })),
  on(TodoActions.toggleTodoStateSuccess, (state, action) =>
    adapter.removeOne(action.todo.id, { ...state, open: Math.max(0, state.open - 1) })
  ),
  on(TodoActions.toggleTodoStateFailure, (state, action) => ({ ...state, error: action.error })),
  on(TodoActions.markAllTodosAsDoneSuccess, (state) => adapter.removeAll({ ...state, open: 0 })),
  on(TodoActions.markAllTodosAsDoneFailure, (state, action) => ({ ...state, error: action.error })),
  on(TodoActions.clearTodos, (state) => adapter.removeAll(state))
);

export const selectTodoState = createFeatureSelector<State>(todosFeatureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectTodoState);

export const selectTodosAll = createSelector(
  selectAll,
  (todos) => todos /*todos.sort((a, b) => a.timestamp.localeCompare(b.timestamp))*/
);
export const selectTodosTotal = selectTotal;
export const selectTodosEntities = selectEntities;
export const selectTodosIds = selectIds;

export const selectTodosAllReversed = createSelector(selectTodosAll, (todos) => todos.reverse());
export const selectTodosLoading = createSelector(selectTodoState, (state) => state.loading);
export const selectTodosOpen = createSelector(selectTodosAll, (todos) => todos);
export const selectTodosOpenOverall = createSelector(selectTodoState, (state) => state.open);
export const selectTodosCount = createSelector(selectTodoState, (state) => state.count);
export const selectTodosLazyParameters = createSelector(selectTodosTotal, (total) => ({
  offset: total,
  limit: 20,
  lastPoll: 0,
}));
export const selectTodosPollingParameters = createSelector(selectTodoState, (state) => ({
  offset: 0, //optional im backend machen
  limit: 20, // ^
  lastPoll: state.lastPoll,
}));
